import type { MailMessage, MailThread } from "@/lib/api";

const INVISIBLE = /[\u034F\u200B-\u200D\u2060\uFEFF\u00AD\u00A0]/g;

function decodeBasicEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
}

function normalizeWs(value: string): string {
  return decodeBasicEntities(value).replace(INVISIBLE, "").replace(/\s+/g, " ").trim();
}

function htmlAsText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ");
}

/** True when the message has a real body — not a list-row snippet stub. */
export function messageHasRealBody(message: MailMessage | null | undefined): boolean {
  if (!message || message.snippetOnly) return false;
  if (message.fullBody) return true;
  const snippet = typeof message.snippet === "string" ? message.snippet : "";
  const snip = normalizeWs(snippet);
  const html = typeof message.bodyHtml === "string" ? message.bodyHtml : "";
  const text =
    (typeof message.bodyText === "string" && message.bodyText) ||
    (typeof (message as MailMessage & { body?: string }).body === "string"
      ? (message as MailMessage & { body?: string }).body || ""
      : "");
  const htmlText = html ? normalizeWs(htmlAsText(html)) : "";
  const flat = normalizeWs(text);
  if (htmlText && (!snip || htmlText !== snip)) return true;
  if (flat && (!snip || flat !== snip)) return true;
  return false;
}

/** IndexedDB / memory detail cache: only a fully expanded thread with real bodies. */
export function shouldCacheMailDetail(thread: MailThread | null | undefined): boolean {
  if (!thread || thread.bodyPending) return false;
  if (!isMailThreadFullyExpanded(thread)) return false;
  const messages = thread.messages || [];
  return messages.length > 0 && messages.every((message) => messageHasRealBody(message));
}

/**
 * True when we have enough body content to paint the reader — but a single
 * list/IDB stub must NOT count (that skipped GET /api/mail/:id and left chains
 * mashed). Prefer {@link isMailThreadFullyExpanded} before skipping network.
 */
export function isMailThreadHydrated(thread: MailThread | null | undefined): boolean {
  if (!thread) return false;
  const messages = thread.messages;
  if (!Array.isArray(messages) || messages.length === 0) return false;
  const declared =
    typeof thread.messageCount === "number" && thread.messageCount > 0
      ? thread.messageCount
      : 0;
  // Incomplete expand — more messages exist than we have shells for.
  if (declared > messages.length) return false;
  return messages.some(messageHasRealBody);
}

/**
 * True only after GET /api/mail/:id marked the chain expanded.
 * A folder list can already hold several real bodies and still be missing
 * sent copies that live in another mailbox, so those lists are not "done".
 */
export function isMailThreadFullyExpanded(thread: MailThread | null | undefined): boolean {
  if (!isMailThreadHydrated(thread) || !thread) return false;
  return thread.detailExpanded === true;
}

/**
 * Mark a detail payload as fully expanded and sync messageCount from the
 * messages we actually have. A higher count left over from the folder list
 * used to stick ("Showing 1 of 3") after the reader only held one body.
 * A truncated provider chain keeps the higher count and is not "done".
 */
export function markThreadDetailExpanded(thread: MailThread): MailThread {
  const messages = Array.isArray(thread.messages) ? thread.messages : [];
  const truncated = Boolean(thread.chainTruncated);
  const declared = typeof thread.messageCount === "number" ? thread.messageCount : 0;
  const count = truncated
    ? Math.max(declared, messages.length, 1)
    : Math.max(messages.length, 1);
  const hasAttachments =
    Boolean(thread.hasAttachments) ||
    messages.some((message) => Array.isArray(message.attachments) && message.attachments.length > 0);
  return {
    ...thread,
    messageCount: count,
    hasAttachments,
    detailExpanded: !truncated,
  } as MailThread & { detailExpanded: boolean };
}
